import { Fragment, useMemo, useState } from 'react';
import {
  formatPrice,
  type SheetCellOutcome,
  type SheetDetail,
  type SheetDetailCell,
  type SheetDetailColumn,
  type SheetDetailRow,
  type SheetPriceRead,
} from '../lib/api';

/**
 * One pull, rendered two ways.
 *
 * The sheet's own shape is a grid — a row per SKU, a column per channel — so
 * "what will this pull do" reads best as that same grid. But "show me every
 * collision" or "sort the price rises by size" reads terribly as a grid and
 * fine as a list. Both are the same payload; only the layout differs, and the
 * one filter state drives both so switching never loses your place.
 *
 * Everything here is client-side over a payload already in hand: no filter
 * change costs a request, and none of it re-decides anything — every verdict
 * shown was decided by the planner and carried through
 * apps/api/src/sheets/detail.ts.
 */

const OUTCOME_LABEL: Record<SheetCellOutcome, string> = {
  stage: 'will change',
  refused: 'not staged',
  blank: 'not priced',
  no_listing: 'no listing',
  skipped: 'skipped',
  blocked: 'blocked',
  currency_mismatch: 'wrong currency',
  unchanged: 'unchanged',
  already_staged: 'already staged',
  collision: 'collision',
  rejected: 'rejected',
};

/** How loudly a cell should read. Four tones, not eleven colours. */
type Tone = 'change' | 'warn' | 'problem' | 'quiet';

const OUTCOME_TONE: Record<SheetCellOutcome, Tone> = {
  stage: 'change',
  refused: 'warn',
  collision: 'problem',
  blocked: 'problem',
  rejected: 'problem',
  currency_mismatch: 'problem',
  skipped: 'quiet',
  unchanged: 'quiet',
  already_staged: 'quiet',
  blank: 'quiet',
  no_listing: 'quiet',
};

const SOURCE_LABEL: Record<string, string> = {
  website: 'Website column',
  override: 'its own column',
  third_party: '3rd Party column',
};

const ROW_STATUS_LABEL: Record<string, string> = {
  unmatched: 'no product with this SKU',
  invalid_sku: 'not a usable SKU',
  duplicate: 'superseded by a later row',
};

const PROBLEM_OUTCOMES: SheetCellOutcome[] = ['collision', 'blocked', 'rejected', 'currency_mismatch', 'skipped'];

type OutcomeFilter = 'all' | 'changes' | 'confirm' | 'problems' | 'quiet' | 'unlinked' | SheetCellOutcome;

type SortKey = 'row' | 'sku' | 'product' | 'channel' | 'current' | 'target' | 'percent' | 'outcome';

/** One row of the flat view: a cell, with its row's identity flattened onto it. */
interface FlatChange {
  row: SheetDetailRow;
  cell: SheetDetailCell;
  column: SheetDetailColumn;
}

export function SheetPreview({ detail }: { detail: SheetDetail }) {
  const [view, setView] = useState<'rows' | 'changes'>('rows');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<'all' | string>('all');
  const [outcome, setOutcome] = useState<OutcomeFilter>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'row', dir: 'asc' });

  const columnById = useMemo(
    () => new Map(detail.columns.map((c) => [c.channelId, c] as const)),
    [detail.columns],
  );

  const matchesSearch = (row: SheetDetailRow): boolean => {
    if (search === '') return true;
    const needle = search.toLowerCase();
    return (
      row.skuDisplay.toLowerCase().includes(needle) ||
      (row.sku ?? '').toLowerCase().includes(needle) ||
      (row.product?.title ?? '').toLowerCase().includes(needle) ||
      String(row.rowNumber) === needle
    );
  };

  const matchesCell = (cell: SheetDetailCell): boolean => {
    if (channel !== 'all' && cell.channelId !== channel) return false;
    switch (outcome) {
      case 'all':
        return true;
      case 'changes':
        return cell.outcome === 'stage' || cell.outcome === 'refused';
      case 'confirm':
        return cell.needsConfirmation;
      case 'problems':
        return PROBLEM_OUTCOMES.includes(cell.outcome);
      case 'quiet':
        return cell.outcome === 'unchanged' || cell.outcome === 'already_staged';
      case 'unlinked':
        return false;
      default:
        return cell.outcome === outcome;
    }
  };

  /**
   * A row survives if any of its cells does. The rows that resolved to nothing
   * have no cells at all, so they answer only to 'all' and to their own filter —
   * "not in the app" is a fact about the row, not about a channel.
   */
  const rows = useMemo(() => {
    return detail.rows.filter((row) => {
      if (!matchesSearch(row)) return false;
      if (row.status !== 'ok') return outcome === 'all' || outcome === 'unlinked';
      if (outcome === 'unlinked') return false;
      return row.cells.some(matchesCell);
    });
  }, [detail.rows, search, channel, outcome]);

  const changes = useMemo(() => {
    const flat: FlatChange[] = [];
    for (const row of detail.rows) {
      if (row.status !== 'ok' || !matchesSearch(row)) continue;
      for (const cell of row.cells) {
        // The flat view is a list of things that happened, so the two outcomes
        // that mean "nothing was said here" are left out unless asked for by
        // name — otherwise every channel a product isn't on fills the list.
        if (outcome === 'all' && (cell.outcome === 'blank' || cell.outcome === 'no_listing')) continue;
        if (!matchesCell(cell)) continue;
        const column = columnById.get(cell.channelId);
        if (column) flat.push({ row, cell, column });
      }
    }
    return sortChanges(flat, sort);
  }, [detail.rows, search, channel, outcome, sort, columnById]);

  const totals = useMemo(() => {
    let willChange = 0;
    let problems = 0;
    let needsConfirmation = 0;
    let unlinked = 0;
    for (const row of detail.rows) {
      if (row.status !== 'ok') unlinked += 1;
      for (const cell of row.cells) {
        if (cell.outcome === 'stage') willChange += 1;
        if (cell.needsConfirmation) needsConfirmation += 1;
        if (PROBLEM_OUTCOMES.includes(cell.outcome)) problems += 1;
      }
    }
    return { willChange, problems, needsConfirmation, unlinked };
  }, [detail.rows]);

  const toggleRow = (rowNumber: number) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });

  const sortBy = (key: SortKey) =>
    setSort((current) => ({ key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc' }));

  if (detail.rows.length === 0) {
    return (
      <div className="empty-state">
        <p>This run read no rows.</p>
        <p className="small">
          Either the {detail.tabName} tab is empty, or every row was excluded by the SKU filter.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="toolbar">
        <div className="view-switch">
          <button className={view === 'rows' ? 'active' : ''} onClick={() => setView('rows')}>
            By sheet row
          </button>
          <button className={view === 'changes' ? 'active' : ''} onClick={() => setView('changes')}>
            By change
          </button>
        </div>

        <input
          type="search"
          placeholder="SKU, product or row number"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            // The native clear (✕) fires a change with an empty value and no
            // Enter, so it has to commit on its own — same as ProductTable.
            if (e.target.value === '') setSearch('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch(searchInput.trim());
          }}
        />

        <select value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="all">Every channel</option>
          {detail.columns.map((c) => (
            <option key={c.channelId} value={c.channelId}>
              {c.channelName}
            </option>
          ))}
        </select>

        <select value={outcome} onChange={(e) => setOutcome(e.target.value as OutcomeFilter)}>
          <option value="all">Everything</option>
          <optgroup label="Groups">
            <option value="changes">Will change ({totals.willChange})</option>
            <option value="confirm">Needs confirmation ({totals.needsConfirmation})</option>
            <option value="problems">Problems ({totals.problems})</option>
            <option value="quiet">Unchanged or already staged</option>
            <option value="unlinked">Not in the app ({totals.unlinked})</option>
          </optgroup>
          <optgroup label="Exact outcome">
            {(Object.keys(OUTCOME_LABEL) as SheetCellOutcome[]).map((kind) => (
              <option key={kind} value={kind}>
                {OUTCOME_LABEL[kind]}
              </option>
            ))}
          </optgroup>
        </select>

        <span className="small muted">
          {view === 'rows'
            ? `${rows.length} of ${detail.rows.length} rows`
            : `${changes.length} change${changes.length === 1 ? '' : 's'}`}
        </span>

        <div className="spacer" />

        {view === 'rows' && (
          <button
            className="link small"
            onClick={() =>
              setExpanded((current) => (current.size > 0 ? new Set() : new Set(rows.map((r) => r.rowNumber))))
            }
          >
            {expanded.size > 0 ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {view === 'rows' ? (
        <RowMatrix
          detail={detail}
          rows={rows}
          expanded={expanded}
          onToggle={toggleRow}
          highlight={outcome !== 'all' || channel !== 'all' ? matchesCell : null}
        />
      ) : (
        <ChangeList changes={changes} sort={sort} onSort={sortBy} />
      )}
    </>
  );
}

/**
 * The grid. One row per sheet row, one column per channel, so a channel's price
 * sits under that channel's own heading and the same field can be compared down
 * a column — the reason ChannelDetail moved from cards to aligned rows too.
 */
function RowMatrix({
  detail,
  rows,
  expanded,
  onToggle,
  highlight,
}: {
  detail: SheetDetail;
  rows: SheetDetailRow[];
  expanded: Set<number>;
  onToggle: (rowNumber: number) => void;
  /** When a filter is on, which cells matched it. Null when nothing is filtered. */
  highlight: ((cell: SheetDetailCell) => boolean) | null;
}) {
  const fullSpan = 4 + detail.columns.length;

  if (rows.length === 0) {
    return <div className="empty-state">No sheet row matches these filters.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 30 }} />
            <th style={{ width: 52 }}>Row</th>
            <th>SKU / product</th>
            <th style={{ width: 74 }}>Site</th>
            {detail.columns.map((c) => (
              <th key={c.channelId} className="price-col">
                {c.channelName}
                {c.overrideHeader && (
                  <div className="small muted" title={`Priced from the "${c.overrideHeader}" column`}>
                    {c.overrideHeader}
                  </div>
                )}
                {!c.enabled && <div className="small muted">disabled</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Fragment key={row.rowNumber}>
              <tr>
                <td>
                  {row.status === 'ok' && (
                    <button
                      className="expand-btn"
                      onClick={() => onToggle(row.rowNumber)}
                      title={expanded.has(row.rowNumber) ? 'Hide the detail' : 'Show what the sheet said'}
                    >
                      {expanded.has(row.rowNumber) ? '−' : '+'}
                    </button>
                  )}
                </td>
                <td className="small muted">{row.rowNumber}</td>
                <td className="title-cell">
                  <div className="sku">{row.skuDisplay}</div>
                  {row.product ? (
                    <div>{row.product.title}</div>
                  ) : (
                    <div className="small">
                      <span className="badge warn">{ROW_STATUS_LABEL[row.status] ?? row.status}</span>
                    </div>
                  )}
                  {row.notes
                    .filter((n) => n.kind !== row.status)
                    .map((n, i) => (
                      <div key={i} className="small muted">
                        {n.message}
                      </div>
                    ))}
                </td>
                <td className="small">{row.product?.site || <span className="muted">—</span>}</td>
                {row.status === 'ok' ? (
                  detail.columns.map((column) => (
                    <ChannelCell
                      key={column.channelId}
                      cells={row.cells.filter((c) => c.channelId === column.channelId)}
                      highlight={highlight}
                    />
                  ))
                ) : (
                  <td colSpan={detail.columns.length} className="small muted">
                    nothing planned for this row
                  </td>
                )}
              </tr>

              {expanded.has(row.rowNumber) && (
                <tr className="sheet-expanded">
                  <td />
                  <td colSpan={fullSpan - 1}>
                    <RowDetail row={row} detail={detail} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One column's worth of a row. Usually a single verdict, but a product can hold
 * two listings on one channel, and the planner decides about each separately —
 * so they stack here rather than one of them going missing.
 */
function ChannelCell({
  cells,
  highlight,
}: {
  cells: SheetDetailCell[];
  highlight: ((cell: SheetDetailCell) => boolean) | null;
}) {
  if (cells.length === 0) {
    return <td className="price-cell sheet-cell quiet" />;
  }

  // The loudest verdict sets the cell's tone: a problem on either listing is
  // the thing worth noticing from across the table.
  const tone = cells.map((c) => OUTCOME_TONE[c.outcome]).sort(byLoudness)[0]!;
  const dimmed = highlight ? !cells.some(highlight) : false;

  return (
    <td className={`price-cell sheet-cell ${tone}${dimmed ? ' dimmed' : ''}`}>
      {cells.map((cell, i) => (
        <Verdict key={cell.listingId ?? i} cell={cell} ordinal={cells.length > 1 ? i + 1 : null} />
      ))}
    </td>
  );
}

/** What one listing shows now, what the sheet asks, and the verdict on the two. */
function Verdict({ cell, ordinal }: { cell: SheetDetailCell; ordinal: number | null }) {
  const changing = cell.outcome === 'stage' || cell.outcome === 'refused';

  return (
    <div title={cell.reason ?? undefined}>
      {cell.outcome === 'no_listing' || cell.outcome === 'blank' ? (
        <span className="muted">—</span>
      ) : changing ? (
        <span className="nowrap">
          <span className="old-value">{formatPrice(cell.currentMinor)}</span>
          <span className="muted"> → </span>
          <strong>{formatPrice(cell.targetMinor)}</strong>
        </span>
      ) : (
        <span className="nowrap">{formatPrice(cell.targetMinor ?? cell.currentMinor)}</span>
      )}

      <div className="small sheet-verdict">
        {ordinal !== null && <span className="muted" title="This product has more than one listing on this channel">listing {ordinal}: </span>}
        {cell.needsConfirmation && <span className="badge warn">confirm</span>}{' '}
        {cell.outcome !== 'blank' && cell.outcome !== 'no_listing' && OUTCOME_LABEL[cell.outcome]}
        {changing && cell.percentChange !== null && ` ${formatPercent(cell.percentChange)}`}
      </div>
    </div>
  );
}

const LOUDNESS: Record<Tone, number> = { problem: 0, warn: 1, change: 2, quiet: 3 };
const byLoudness = (a: Tone, b: Tone) => LOUDNESS[a] - LOUDNESS[b];

/**
 * What the sheet literally said, and how it was wired. The point of this panel
 * is that a wrong price should be traceable to a cell without opening the sheet
 * — so it shows the raw cells, which column fed each channel, and the planner's
 * own words for every channel that did not take.
 */
function RowDetail({ row, detail }: { row: SheetDetailRow; detail: SheetDetail }) {
  const overrideHeaderFor = (channelId: string) =>
    detail.columns.find((c) => c.channelId === channelId)?.overrideHeader ?? channelId;

  return (
    <div className="sheet-row-detail">
      <div>
        <h4>Cells as read, row {row.rowNumber}</h4>
        <dl>
          <dt>Website</dt>
          <dd>
            <ReadCell value={row.read.website} />
          </dd>
          <dt>3rd Party</dt>
          <dd>
            <ReadCell value={row.read.thirdParty} />
          </dd>
          {Object.entries(row.read.overrides).map(([channelId, value]) => (
            <Fragment key={channelId}>
              <dt>{overrideHeaderFor(channelId)}</dt>
              <dd>
                <ReadCell value={value} />
              </dd>
            </Fragment>
          ))}
        </dl>
      </div>

      <div>
        <h4>Matched product</h4>
        {row.product ? (
          <dl>
            <dt>Title</dt>
            <dd>{row.product.title}</dd>
            <dt>SKU</dt>
            <dd className="sku">{row.product.sku}</dd>
            <dt>Site</dt>
            <dd>{row.product.site || '—'}</dd>
            <dt>Product id</dt>
            <dd className="sku">{row.product.id}</dd>
          </dl>
        ) : (
          <p className="small muted">This row resolved to no product.</p>
        )}
      </div>

      <div>
        <h4>Per channel</h4>
        <dl>
          {row.cells.map((cell, i) => {
            const column = detail.columns.find((c) => c.channelId === cell.channelId);
            const sameChannel = row.cells.filter((c) => c.channelId === cell.channelId);
            const ordinal = sameChannel.length > 1 ? ` (listing ${sameChannel.indexOf(cell) + 1})` : '';
            return (
              <Fragment key={cell.listingId ?? `${cell.channelId} ${i}`}>
                <dt>
                  {column?.channelName ?? cell.channelId}
                  {ordinal}
                </dt>
                <dd>
                  {OUTCOME_LABEL[cell.outcome]}
                  {cell.from && <span className="muted small"> · from the {SOURCE_LABEL[cell.from]}</span>}
                  {cell.reason && <div className="small muted">{cell.reason}</div>}
                </dd>
              </Fragment>
            );
          })}
        </dl>
      </div>
    </div>
  );
}

/** A raw sheet cell, keeping blank and zero and error visibly distinct. */
function ReadCell({ value }: { value: SheetPriceRead }) {
  if (value.kind === 'blank') return <span className="muted">blank</span>;
  if (value.kind === 'error') return <span className="badge error">{value.reason}</span>;
  return <span>{formatPrice(value.minor)}</span>;
}

/**
 * The flat view. Same data, one line per (row, channel) — which is the shape
 * you want when the question is "show me every collision" rather than "what
 * happens to this SKU".
 */
function ChangeList({
  changes,
  sort,
  onSort,
}: {
  changes: FlatChange[];
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
}) {
  if (changes.length === 0) {
    return <div className="empty-state">No change matches these filters.</div>;
  }

  const header = (key: SortKey, label: string, extra = '') => (
    <th className={`sortable ${extra}`} onClick={() => onSort(key)} title={`Sort by ${label.toLowerCase()}`}>
      {label}
      {sort.key === key && <span className="sort-caret">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            {header('row', 'Row')}
            {header('sku', 'SKU')}
            {header('product', 'Product')}
            {header('channel', 'Channel')}
            <th>Priced from</th>
            {header('current', 'Current')}
            {header('target', 'Sheet')}
            {header('percent', 'Change')}
            {header('outcome', 'Outcome')}
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {changes.map(({ row, cell, column }) => (
            <tr key={`${row.rowNumber} ${cell.listingId ?? cell.channelId}`}>
              <td className="small muted">{row.rowNumber}</td>
              <td className="sku">{row.skuDisplay}</td>
              <td className="title-cell">{row.product?.title}</td>
              <td className="nowrap">
                <span className={`pill pill-${siteClass(column.channelType)}`}>{column.channelName}</span>
              </td>
              <td className="small muted nowrap">{cell.from ? SOURCE_LABEL[cell.from] : '—'}</td>
              <td className="nowrap">{formatPrice(cell.currentMinor)}</td>
              <td className="nowrap">
                <strong>{formatPrice(cell.targetMinor)}</strong>
              </td>
              <td className={`nowrap small ${cell.percentChange === null ? 'muted' : ''}`}>
                {cell.percentChange === null ? '—' : formatPercent(cell.percentChange)}
              </td>
              <td className="nowrap">
                <span className={`badge ${badgeClass(OUTCOME_TONE[cell.outcome])}`}>
                  {OUTCOME_LABEL[cell.outcome]}
                </span>
                {cell.needsConfirmation && <span className="badge warn">confirm</span>}
              </td>
              <td className="small muted">{cell.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Mirrors ChannelTag's mapping, for the one place a pill is built from a column. */
const siteClass = (type: string) =>
  type === 'drupal_uc' ? 'mlf' : type === 'drupal_commerce' ? 'mff' : 'market';

const badgeClass = (tone: Tone) => (tone === 'problem' ? 'error' : tone === 'warn' ? 'warn' : tone === 'change' ? 'ok' : '');

const formatPercent = (percent: number) => `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;

function sortChanges(changes: FlatChange[], sort: { key: SortKey; dir: 'asc' | 'desc' }): FlatChange[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const value = ({ row, cell, column }: FlatChange): string | number => {
    switch (sort.key) {
      case 'row':
        return row.rowNumber;
      case 'sku':
        return row.skuDisplay.toLowerCase();
      case 'product':
        return (row.product?.title ?? '').toLowerCase();
      case 'channel':
        return column.channelName.toLowerCase();
      case 'current':
        return cell.currentMinor ?? -1;
      case 'target':
        return cell.targetMinor ?? -1;
      case 'percent':
        // Sorted by size of the move, not by sign: "biggest change" is the
        // question being asked, and a 40% cut belongs next to a 40% rise.
        return cell.percentChange === null ? -1 : Math.abs(cell.percentChange);
      case 'outcome':
        return OUTCOME_LABEL[cell.outcome];
    }
  };

  return [...changes].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left === right) return a.row.rowNumber - b.row.rowNumber;
    return (left < right ? -1 : 1) * sign;
  });
}
